import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Layout, { PageHeader } from "@/components/Layout";
import { toast } from "sonner";
import { Plus, FlaskConical, Rocket, Trash2, Layers, Pencil } from "lucide-react";

const badge = { draft: "bg-slate-100 text-slate-600", paper: "bg-amber-50 text-amber-700", live: "bg-emerald-50 text-emerald-700" };

export default function Strategies() {
  const [list, setList] = useState([]);
  const nav = useNavigate();

  const load = () => api.get("/strategies").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const remove = async (id) => { await api.delete(`/strategies/${id}`); toast.success("Deleted"); load(); };

  return (
    <Layout>
      <PageHeader title="My Strategies" subtitle="All saved & deployed strategies"
        right={<button data-testid="new-strategy-btn" onClick={() => nav("/builder")}
          className="h-9 px-4 bg-blue-600 text-white text-sm font-semibold flex items-center gap-2 hover:bg-blue-700 transition-colors">
          <Plus className="w-4 h-4" /> New Strategy</button>} />
      <div className="min-w-0 p-4 sm:p-6 lg:p-8">
        {list.length === 0 ? (
          <div className="border border-dashed border-slate-300 p-16 text-center">
            <Layers className="w-8 h-8 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500 text-sm mb-4">No strategies yet.</p>
            <button onClick={() => nav("/builder")} className="h-9 px-4 bg-slate-900 text-white text-sm font-semibold">Build your first strategy</button>
          </div>
        ) : (
          <div className="max-w-full overflow-x-auto border border-slate-200 bg-white">
            <table className="w-full min-w-[900px] text-sm">
              <thead>
                <tr className="text-left text-xs font-bold tracking-wide uppercase text-slate-500 border-b border-slate-200">
                  <th className="px-6 py-3">Name</th><th className="px-6 py-3">Underlying</th>
                  <th className="px-6 py-3">Sentiment</th><th className="px-6 py-3 text-right">Legs</th>
                  <th className="px-6 py-3 text-right">Capital</th><th className="px-6 py-3">Status</th>
                  <th className="px-6 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.map((s) => (
                  <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`strategy-row-${s.id}`}>
                    <td className="px-6 py-3 font-semibold">{s.name}</td>
                    <td className="px-6 py-3 font-mono">{s.underlying}</td>
                    <td className="px-6 py-3 text-slate-600">{s.market_sentiment}</td>
                    <td className="px-6 py-3 text-right font-mono">{s.positions?.length || 0}</td>
                    <td className="px-6 py-3 text-right font-mono">₹{Number(s.capital).toLocaleString("en-IN")}</td>
                    <td className="px-6 py-3"><span className={`text-xs font-semibold px-2 py-1 ${badge[s.status] || badge.draft}`}>{s.status.toUpperCase()}</span></td>
                    <td className="px-6 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button title="Edit" data-testid={`edit-${s.id}`} onClick={() => nav(`/builder/${s.id}`)}
                          className="w-8 h-8 flex items-center justify-center border border-slate-300 hover:border-slate-900 hover:text-slate-900 transition-colors"><Pencil className="w-4 h-4" /></button>
                        <button title="Backtest" data-testid={`backtest-${s.id}`} onClick={() => nav(`/backtest?strategy=${s.id}`)}
                          className="w-8 h-8 flex items-center justify-center border border-slate-300 hover:border-blue-600 hover:text-blue-600 transition-colors"><FlaskConical className="w-4 h-4" /></button>
                        <button title="Deploy" data-testid={`deploy-${s.id}`} onClick={() => nav(`/autotrade?strategy=${s.id}`)}
                          className="w-8 h-8 flex items-center justify-center border border-slate-300 hover:border-emerald-600 hover:text-emerald-600 transition-colors"><Rocket className="w-4 h-4" /></button>
                        <button title="Delete" onClick={() => remove(s.id)}
                          className="w-8 h-8 flex items-center justify-center border border-slate-300 hover:border-rose-500 hover:text-rose-500 transition-colors"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
